import { ref, unref, computed, watch, onUnmounted, onActivated, onDeactivated } from '@vue/composition-api'
import dayjs from 'dayjs'

/**
 * @typedef {import('@vue/composition-api').Ref<T>} Ref
 * @template T
 */

/**
 * Parse `playedTime` to minute and second
 * @param {string} playedTime 比赛进行时间, 格式为分'秒", 如 09'55"
 */
const parsePlayedTime = (playedTime) => {
  if (!playedTime)
    return {
      minute: 0,
      second: 0
    }

  const [minute, second] = playedTime.match(/\d+/g)
  return {
    minute: +minute,
    second: +second
  }
}

/**
 * 格式化时间, 格式为分'秒", 如 09'55"
 * @param {object} options
 * @param {number | string} options.minute
 * @param {number | string} options.second
 */
const getFormattedTime = (options = {}) => {
  const { minute, second, liveTimeType = 1 } = options

  if (liveTimeType === 1) {
    return `${minute}'${second < 10 ? '0' + second : second}"`
  }

  return `${minute}:${second < 10 ? '0' + second : second}`
}

/**
 * 足球走秒计算
 * @param {object} options
 * @param {string} options.showTime 比赛进行时间, 格式为分'秒", 如 09'55"
 * @param {number} options.liveTimeType default `1`
 * @param {number | string} options.stoppageTime
 * @return {string}
 */
const calcSeconds = (options = {}) => {
  const { showTime = '', liveTimeType = 1 } = options

  if (!showTime) return ''

  let { minute, second } = parsePlayedTime(showTime)

  second = second + 1
  if (second >= 60) {
    second = 0
    minute = minute + 1
  }

  return getFormattedTime({
    minute,
    second,
    liveTimeType
  })
}

// 判断是否是 live match
const isLiveMatch = (status) => status === 1 || status === 2

// 解析 match time
export const getMatchTime = ({ status, totalPlayedTime, sectionRemainTime, startTime }, pattern = 'HH:mm', liveTimeType) => {
  const isLive = isLiveMatch(status)
  if (isLive) {
    const time = totalPlayedTime || sectionRemainTime
    if (!time) return ''

    const { minute, second } = parsePlayedTime(totalPlayedTime || sectionRemainTime)
    return getFormattedTime({ minute, second, liveTimeType })
  }
  return startTime ? dayjs(startTime).format(pattern) : ''
}

export const getFormattedStoppageTime = (event = {}) => {
  if (!event.stoppageTime) return null

  // already formatted
  if (/^\(\+\S*\)$/.test(event.stoppageTime)) return event.stoppageTime

  return `(+${event.stoppageTime}')`
}

// realsport 比赛列表单场比赛时间
/**
 *
 * @param {object}  event             比赛对象
 * @param {object}  options   日期格式
 * @param {string}  options.pattern   日期格式
 * @param {boolean} options.autoplay  是否自动走秒
 * @param {number} options.liveTimeType  live比赛返回时间格式 1: 分'秒" 2：分:秒
 * @returns {{
 *  showTime: Ref<string>
 *  formattedStoppageTime: Ref<string | null>
 * }} 比赛时间; if `formattedStoppageTime` is null, it means the match is not in stoppageTime
 */
export default function useMatchTime(event, options = {}) {
  const { pattern = 'HH:mm', autoplay = true, sportId, liveTimeType = 1 } = options

  const sportIdValue = unref(event).sportId || unref(sportId)
  const { startTime } = unref(event)
  const isLive = computed(() => isLiveMatch(unref(event).status))

  const stoppageTime = computed(() => {
    return unref(event).stoppageTime
  })

  const formattedStoppageTime = computed(() => {
    if (!stoppageTime.value) return null

    // already formatted
    if (/^\(\+\S*\)$/.test(stoppageTime.value)) return stoppageTime.value

    return `(+${stoppageTime.value}')`
  })

  // 比赛时间, live 比赛为进行时间，prematch 比赛为开始时间
  const matchTime = computed(() => {
    return getMatchTime(
      {
        status: unref(event).status,
        totalPlayedTime: unref(event).totalPlayedTime,
        sectionRemainTime: unref(event).sectionRemainTime,
        startTime
      },
      pattern,
      liveTimeType
    )
  })

  // 比赛展示时间
  const showTime = ref(matchTime.value)

  // sync `showTime` with `matchTime`
  watch(matchTime, (val) => {
    showTime.value = val
  })

  // soccer live time 走秒
  if (autoplay && (sportIdValue === 'sr:sport:1' || sportIdValue === 'lv:sport:1')) {
    let matchTimer = null

    const updateLiveTime = () => {
      const newTime = calcSeconds({
        showTime: showTime.value,
        liveTimeType
      })
      showTime.value = newTime
    }

    const stopTimer = () => {
      matchTimer && clearInterval(matchTimer)
      matchTimer = null
    }

    const startTimer = () => {
      stopTimer()
      const intervalTime = sportIdValue === 'lv:sport:1' ? 1000 / 3 : 1000
      matchTimer = setInterval(updateLiveTime, intervalTime)
    }

    if (isLive.value) {
      onUnmounted(() => {
        stopTimer()
      })

      onActivated(() => {
        shouldStartTimer.value && startTimer()
      })

      onDeactivated(() => {
        stopTimer()
      })
    }

    const shouldStartTimer = computed(() => {
      if (!unref(event).totalPlayedTime || !isLive.value) {
        return false
      }

      const parsedShowTime = parsePlayedTime(showTime.value)
      if (!stoppageTime.value && (parsedShowTime.minute === 45 || parsedShowTime.minute === 90 || parsedShowTime.minute === 105 || parsedShowTime.minute === 120) && parsedShowTime.second === 0) {
        return false
      }

      return true
    })

    watch(
      () => shouldStartTimer.value,
      (val) => {
        if (val) {
          startTimer()
          return
        }

        stopTimer()
      },
      {
        immediate: true
      }
    )
  }

  return {
    showTime,
    formattedStoppageTime
  }
}
